/* eslint-disable @typescript-eslint/no-floating-promises */
import {SiteStore} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/site-store/SiteStore';
import {errorInCheckoutSfParams} from '@wix/bi-logger-ec-sf';
import {ICart, IPaymentMethod} from '@wix/wixstores-graphql-schema/dist/es/src';
import {ContinueShoppingBiOrigin, EMPTY_CART_ID, ORIGIN, PaymentMethodType} from '../../components/cart/constants';
import {CartType} from '@wix/wixstores-client-core/dist/es/src/types';
import {ShippingRuleStatus} from '@wix/wixstores-client-core/dist/src/types/shipping-rule-status';

type SetDestinationBIData = {
  cartId: string;
  cartType: string;
  shippingDestinationCountry?: string;
  shippingDestinationState?: string;
};

export class BIService {
  private readonly siteStore: SiteStore;

  constructor({siteStore}: {siteStore: SiteStore}) {
    this.siteStore = siteStore;
  }

  private baseBiParams(cart: ICart): {origin: string; cartId?: string; storeId?: string} {
    return {
      origin: ORIGIN,
      cartId: !cart || cart.cartId === EMPTY_CART_ID ? undefined : cart.cartId,
      storeId: cart?.storeId,
    };
  }

  public clickOnShippingDestinationInCartPageSf(cart: ICart, cartType: string): void {
    this.siteStore.biLogger.clickOnShippingDestinationInCartPageSf({
      ...this.baseBiParams(cart),
      ...BIService.setDestinationBIData(cart, cartType),
    });
  }

  public clickOnCheckoutWithEWalletSf(cart: ICart, cartType: string, paymentMethods: IPaymentMethod[] = []): void {
    this.siteStore.biLogger.clickOnCheckoutWithEWalletSf({
      ...this.baseBiParams(cart),
      ...BIService.setDestinationBIData(cart, cartType),
      is_with_ewallet_payment: true,
      paymet_providers: this.stringifyPaymentMethods(paymentMethods),
    });
  }

  public clickContinueEWalletModalSf(cart: ICart, cartType: string, paymentMethods: IPaymentMethod[] = []): void {
    this.siteStore.biLogger.clickContinueEWalletModalSf({
      ...this.baseBiParams(cart),
      cartType,
      paymet_providers: this.stringifyPaymentMethods(paymentMethods),
    });
  }

  private stringifyPaymentMethods(paymentMethods: IPaymentMethod[]) {
    return paymentMethods.map((m) => m.name).join(',');
  }

  public updateClickedInSelectYourShippingDestinationPopupSf(
    cart: ICart,
    cartType: string,
    changedDestination: {country: string; subdivision: string; zipCode: string}
  ): void {
    this.siteStore.biLogger.updateClickedInSelectYourShippingDestinationPopupSf({
      ...this.baseBiParams(cart),
      shippingDestinationCountry: changedDestination.country,
      shippingDestinationState: changedDestination.subdivision,
      hasZipCode: !!changedDestination.zipCode,
      cartId: cart.cartId,
      cartType,
    });
  }

  public errorInCheckoutSf(cart: ICart): void {
    const {destination} = cart;
    const subdivision = destination.subdivision ? ` - ${destination.subdivision}` : /* istanbul ignore next */ '';
    const destinationStr = `${destination.country}${subdivision}`;
    const params: errorInCheckoutSfParams = {
      ...this.baseBiParams(cart),
      field: 'destination',
      message: `cannot ship to destination ${destinationStr}`,
      stage: 'cart',
    };
    this.siteStore.biLogger.errorInCheckoutSf(params);
  }

  public cancelClickedInSelectYourShippingDestinationPopupSf(cart: ICart, cartType: CartType): void {
    this.siteStore.biLogger.cancelClickedInSelectYourShippingDestinationPopupSf({
      ...this.baseBiParams(cart),
      cartType,
    });
  }

  public cartClickOnContinueShoppingSf(cart: ICart, origin: ContinueShoppingBiOrigin): void {
    this.siteStore.biLogger.cartClickOnContinueShoppingSf({
      ...this.baseBiParams(cart),
      origin,
    });
  }

  public minimumOrderMessageIsShownInCart(cart: ICart, buttonEnabled: boolean): void {
    this.siteStore.biLogger.minimumOrderMessageIsShownInCart({
      ...this.baseBiParams(cart),
      currency: cart.currencyFormat.code,
      minimumValueLong: cart.minimumOrderAmount.value,
      diffLong: cart.minimumOrderAmount.diff,
      buttonEnabled,
    });
  }

  public clickOnAddNoteToSellerSf(cart: ICart): void {
    this.siteStore.biLogger.clickOnAddNoteToSellerSf({
      ...this.baseBiParams(cart),
    });
  }

  public clickOnApplyPromotionalCodeSf(cart: ICart): void {
    this.siteStore.biLogger.clickOnApplyPromotionalCodeSf({
      ...this.baseBiParams(cart),
    });
  }

  public errorWhenApplyingACouponSf(cart: ICart, couponCode: string, errorCode: string): void {
    this.siteStore.biLogger.errorWhenApplyingACouponSf({
      ...this.baseBiParams(cart),
      couponCode,
      errorCode,
    });
  }

  public viewCartPageSf({
    cart,
    cartType,
    isEligibleForCheckoutInViewer,
    paymentMethods,
    numOfVisibleShippingOptions,
    shouldShowCoupon,
    shouldShowBuyerNote,
    shouldShowContinueShopping,
    shouldShowShipping,
    shouldShowTax,
    hasPickupOption,
  }: {
    cart: ICart;
    cartType: string;
    isEligibleForCheckoutInViewer: boolean;
    paymentMethods: IPaymentMethod[];
    numOfVisibleShippingOptions: number;
    shouldShowCoupon: boolean;
    shouldShowBuyerNote: boolean;
    shouldShowContinueShopping: boolean;
    shouldShowShipping: boolean;
    shouldShowTax: boolean;
    hasPickupOption: boolean;
  }): void {
    const isShippingCalculated = cart?.shippingRuleInfo?.status === ShippingRuleStatus.OK;

    this.siteStore.biLogger.viewCartPageSf({
      ...this.baseBiParams(cart),
      cartType,
      checkoutType: isEligibleForCheckoutInViewer ? 'viewer' : 'not in viewer',
      isContinueShopping: shouldShowContinueShopping,
      isDeliveryRates: shouldShowShipping,
      isPickupAvailable: hasPickupOption,
      isShippingCalculated,
      isTaxCosts: shouldShowTax,
      is_note: shouldShowBuyerNote,
      is_promo_code: shouldShowCoupon,
      is_with_ewallet_payment: paymentMethods.map((m) => m.type).includes(PaymentMethodType.eWallet),
      is_with_offline_payment: paymentMethods.map((m) => m.type).includes(PaymentMethodType.offline),
      itemsCount: cart?.items.reduce((count, item) => count + item.quantity, 0) || 0,
      lineItemsCount: cart?.items.length || 0,
      num_of_paymet_providers: paymentMethods.length,
      num_of_shipping: numOfVisibleShippingOptions,
      paymet_providers: paymentMethods.map((m) => m.name).join(','),
      shippingReason: isShippingCalculated ? '' : cart?.shippingRuleInfo?.status || 'unknown',
      viewMode: this.siteStore.viewMode,
    });
  }

  private static setDestinationBIData(cart: ICart, cartType: string) {
    const biData: SetDestinationBIData = {
      cartId: cart.cartId,
      cartType,
    };
    if (cart.destination) {
      biData.shippingDestinationCountry = cart.destination.country;
      biData.shippingDestinationState = cart.destination.subdivision;
    }
    return biData;
  }
}
